import { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Download, DatabaseBackup, FileText } from "lucide-react";

export default function AdminData() {
  const [backups, setBackups] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/backup/list").then(r => setBackups(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runBackup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/backup/run"); toast.success(`Backup complete — ${Object.keys(data.stats).length} collections`); load(); }
    catch (e) { toast.error("Backup failed"); }
    finally { setBusy(false); }
  };
  const downloadPdf = async () => {
    const res = await fetch(`${API}/export/executive-snapshot.pdf?period_days=7`, { credentials: "include" });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `gram_executive_snapshot.pdf`; a.click();
    URL.revokeObjectURL(url); toast.success("PDF downloaded");
  };
  const downloadXlsx = async () => {
    const res = await fetch(`${API}/export/full.xlsx`, { credentials: "include" });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `gram_full_export.xlsx`; a.click();
    URL.revokeObjectURL(url); toast.success("XLSX downloaded");
  };

  return (
    <div className="space-y-6 animate-in">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Administration</div>
        <h1 className="font-serif-display text-3xl">Data & Backup Management</h1>
        <p className="text-sm text-muted-foreground mt-1">Automatic daily backups run at 03:00 IST. Snapshots older than 30 days are pruned.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-3"><DatabaseBackup className="text-accent"/><div className="font-serif-display text-lg">Run Backup Now</div></div>
          <p className="text-xs text-muted-foreground mb-4">Writes JSON of all collections + full XLSX to <code>/app/backups</code> and emails the admin.</p>
          <Button onClick={runBackup} disabled={busy} data-testid="run-backup-btn">Run Backup</Button>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-3"><FileText className="text-accent"/><div className="font-serif-display text-lg">Executive PDF</div></div>
          <p className="text-xs text-muted-foreground mb-4">Single-page Monday snapshot to forward to Partners / Investors. No internal names beyond top performers.</p>
          <Button onClick={downloadPdf} data-testid="exec-pdf-btn"><Download size={14} className="mr-1"/> Download PDF</Button>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-3"><Download className="text-accent"/><div className="font-serif-display text-lg">Full XLSX Export</div></div>
          <p className="text-xs text-muted-foreground mb-4">10-sheet workbook linked by IDs — all clients, leads, visits, follow-up remarks and tags.</p>
          <Button onClick={downloadXlsx} variant="outline" data-testid="full-xlsx-btn"><Download size={14} className="mr-1"/> Download XLSX</Button>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="p-4 text-[11px] uppercase tracking-widest text-muted-foreground">Recent Backups</div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Path</th><th className="text-right px-4 py-3">Files</th><th className="text-right px-4 py-3">Size</th></tr>
          </thead>
          <tbody>
            {backups.map(b => (
              <tr key={b.date} className="border-t border-border">
                <td className="px-4 py-3">{b.date}</td>
                <td className="px-4 py-3 font-mono text-xs">{b.path}</td>
                <td className="px-4 py-3 text-right metric-value">{b.files}</td>
                <td className="px-4 py-3 text-right metric-value">{b.size_kb} KB</td>
              </tr>
            ))}
            {backups.length === 0 && <tr><td colSpan="4" className="px-4 py-10 text-center text-muted-foreground">No backups yet — click "Run Backup" above.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
